import React, {useEffect, useRef} from "react";
import {AButton, ADrawerContent, AIcon} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {t} from "i18next";
import SeverityChip from "features/incidents/IncidentsTable/SeverityChip";
import AnimatePresence from "components/AnimatePresence/AnimatePresence";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  EntitySection,
  TextSection
} from "components/Drawer";
import DrawerTitle from "components/Drawer/components/DrawerTitle/DrawerTitle";
import {EntityList} from "components/EntityList";
import {ENTITY_TYPE} from "utils/entities";
import ModuleSourceSection from "./ModuleSourceSection";
import ObservedTime from "./ObservedTime";
import css from "./IncidentDetectionDrawer.module.scss";

const entityListProps = {
  showEventsCount: false,
  limit: 10
};

const IncidentDetectionDrawer = ({
  event,
  overflowLayersCount,
  addOverflowContent,
  removeOverflowContent,
  overflowContent,
  isOpen,
  onClose
}) => {
  const overflowHeaderRef = useRef();

  // Force the top of the drawer into view so that
  // the bottom of the entity list is not the default
  // view when opening overflow content
  useEffect(() => {
    if (overflowHeaderRef.current) {
      overflowHeaderRef.current.scrollIntoView();
    }
  }, [overflowLayersCount]);

  /**
   * @todo Overflow content can probably be a component.
   */
  const getOverflowContent = (entityType, data) => (
    <>
      <DrawerHeader
        className={classNames(css.header, css.overflowHeader)}
        hideDivider={true}>
        <AButton
          tertiaryAlt
          icon
          className={css.backBtn}
          onClick={removeOverflowContent}>
          <AIcon>caret-left</AIcon>
        </AButton>
        <div>
          <DrawerTitle
            ref={overflowHeaderRef}
            data-testid="DetectionDrawerTitle"
            title={event?.title}
            className="mb-0"
          />
          <span className={css.overflowCount}>
            {data.length} {entityType}
          </span>
        </div>
      </DrawerHeader>
      <DrawerBody>
        <ADrawerContent className={css.overflowContent}>
          <EntityList
            entityType={entityType}
            data={data}
            count={data.length}
            limit={null}
          />
        </ADrawerContent>
      </DrawerBody>
    </>
  );

  const baseContent = (
    <>
      <DrawerHeader className={css.header}>
        <DrawerTitle data-testid="DetectionDrawerTitle" title={event?.title} />
        <ObservedTime startTime={event?.observed_time?.start_time} />
        <SeverityChip severity={event?.severity} className="mb-2" />
      </DrawerHeader>
      <DrawerBody>
        <ModuleSourceSection module={event?.module} source={event?.source} />

        <TextSection
          title={t("drawers.labels.common.shortDescription")}
          text={event?.short_description}
        />

        <TextSection
          title={t("drawers.labels.common.longDescription")}
          text={event?.description}
        />

        <EntitySection
          entityType={ENTITY_TYPE.observables}
          data={event?.observables}
          count={event?.observables?.length}
          entityListProps={{
            ...entityListProps,
            plusMoreHandler: () =>
              addOverflowContent(
                getOverflowContent(ENTITY_TYPE.observables, event?.observables)
              )
          }}
        />

        <EntitySection
          entityType={ENTITY_TYPE.assets}
          data={event?.targets}
          count={event?.targets?.length}
          entityListProps={{
            ...entityListProps,
            plusMoreHandler: () =>
              addOverflowContent(
                getOverflowContent(ENTITY_TYPE.assets, event?.assets)
              )
          }}
        />

        <EntitySection
          entityType={ENTITY_TYPE.indicators}
          data={event?.indicators}
          count={event?.indicators?.length}
          entityListProps={{
            ...entityListProps,
            plusMoreHandler: () =>
              addOverflowContent(
                getOverflowContent(ENTITY_TYPE.indicators, event?.indicators)
              )
          }}
        />

        <EntitySection
          entityType={ENTITY_TYPE.relations}
          data={event?.relations}
          count={event?.relations?.length}
          entityListProps={{
            ...entityListProps,
            plusMore: ({handleClick}) => (
              <a onClick={handleClick}>
                {t("detection.drawer.sections.relations.viewAllBtn")}
              </a>
            ),
            plusMoreHandler: () =>
              addOverflowContent(
                getOverflowContent(ENTITY_TYPE.relations, event?.relations)
              )
          }}
        />
      </DrawerBody>
    </>
  );

  const drawerContent = overflowContent ? (
    <AnimatePresence>{overflowContent}</AnimatePresence>
  ) : (
    baseContent
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} position="fixed">
      {event ? drawerContent : null}
    </Drawer>
  );
};

export default IncidentDetectionDrawer;
